import type { CategoryNavMetadataDto } from '@_/api-contracts/public-api.ts';
import type { VanillaLinkProps } from '@_/ui';
import { PiBook, PiExam } from 'react-icons/pi';
import { FormattedMessage } from 'react-intl';
import { Link } from '@_/ui';

export function CategoryNavPopover({
  categorySlug,
  contents,
  onClose
}: {
  categorySlug: string;
  contents: CategoryNavMetadataDto['contents'];
  onClose: () => void;
}) {
  const { exams, subjects } = contents;

  return (
    <div className="grid grid-cols-2 divide-x divide-gray-100">
      {!!subjects?.length && (
        <Section
          heading={<FormattedMessage id="heading.subjects" defaultMessage="Subjects" />}
          icon={<PiBook />}
        >
          <div className="-mx-1 flex flex-wrap">
            {subjects.map(topic => (
              <TopicLink
                key={topic.id}
                categorySlug={categorySlug}
                onClick={onClose}
                topicSlug={topic.slug}
              >
                {topic.name}
              </TopicLink>
            ))}
          </div>
        </Section>
      )}

      {!!exams?.length && (
        <Section
          heading={<FormattedMessage id="heading.exam-prep" defaultMessage="Exam Prep" />}
          icon={<PiExam />}
        >
          <div className="-mx-1 flex flex-wrap">
            {exams.map(topic => (
              <TopicLink
                key={topic.id}
                categorySlug={categorySlug}
                onClick={onClose}
                topicSlug={topic.slug}
              >
                {topic.name}
              </TopicLink>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({
  children,
  heading,
  icon
}: React.PropsWithChildren<{
  heading: React.ReactNode;
  icon: React.ReactNode;
}>) {
  return (
    <div className="p-6">
      <header className="flex items-center text-lg">
        <span className="mr-2">{icon}</span>
        <span>{heading}</span>
      </header>
      <div className="mt-6">{children}</div>
    </div>
  );
}

function TopicLink({
  categorySlug,
  topicSlug,
  ...rest
}: VanillaLinkProps & {
  categorySlug: string;
  topicSlug: string;
}) {
  return (
    <Link
      {...rest}
      className="mx-1 my-1 block rounded-full border border-gray-200 p-1 px-3 hover:border-primary-600 hover:text-primary-600"
      from="/{-$locale}"
      to="/{-$locale}/courses"
      search={{ topic: topicSlug }}
      plain
    />
  );
}
